import { z } from 'zod';
import type { TemplateKind } from './enums';

/**
 * HỢP ĐỒNG DỮ LIỆU MẪU BÁO GIÁ — QuoteTemplateContext
 *
 * Quy tắc chung (phiên bản 1):
 *  - Mọi giá trị hiển thị đều là CHUỖI đã định dạng sẵn bởi backend; template không tự tính toán.
 *  - money : "125.000.000 ₫" (dấu chấm phân cách nghìn, làm tròn tới đồng)
 *  - number: định dạng Việt Nam "5,5" / "1.234"
 *  - date  : "dd/mm/yyyy" theo múi giờ Asia/Ho_Chi_Minh
 *  - image : data URI (data:image/...;base64,...) hoặc "" khi không có ảnh — luôn kiểm tra bằng {{#if}}
 *  - Giá trị rỗng: chuỗi "" (không bao giờ là null/undefined), boolean dùng cho điều kiện hiển thị.
 *  - Escaping: {{biến}} luôn được escape HTML. Cú pháp {{{ }}} (không escape) bị CẤM.
 *  - Biến không có trong hợp đồng → lỗi khi tải lên / xem trước (strict mode).
 */
export const TEMPLATE_CONTRACT_VERSION = '1.0';

export type LeafType = 'string' | 'money' | 'date' | 'number' | 'boolean' | 'image' | 'percent';
export type VarNode =
  | { type: LeafType; desc: string; example?: string }
  | { type: 'object'; desc: string; fields: Record<string, VarNode> }
  | { type: 'array'; desc: string; item: VarNode };

const s = (desc: string, example?: string): VarNode => ({ type: 'string', desc, example });
const m = (desc: string, example = '125.000.000 ₫'): VarNode => ({ type: 'money', desc, example });
const d = (desc: string, example = '18/10/2026'): VarNode => ({ type: 'date', desc, example });
const n = (desc: string, example?: string): VarNode => ({ type: 'number', desc, example });
const p = (desc: string, example = '5%'): VarNode => ({ type: 'percent', desc, example });
const b = (desc: string): VarNode => ({ type: 'boolean', desc });
const img = (desc: string): VarNode => ({ type: 'image', desc, example: 'data:image/png;base64,...' });
const obj = (desc: string, fields: Record<string, VarNode>): VarNode => ({ type: 'object', desc, fields });
const arr = (desc: string, item: VarNode): VarNode => ({ type: 'array', desc, item });

export const TEMPLATE_CONTRACT: Record<string, VarNode> = {
  company: obj('Thông tin doanh nghiệp (Cài đặt → Thông tin doanh nghiệp)', {
    name: s('Tên doanh nghiệp'),
    shortName: s('Tên viết tắt'),
    address: s('Địa chỉ'),
    phone: s('Điện thoại'),
    hotline: s('Hotline'),
    email: s('Email'),
    website: s('Website'),
    taxCode: s('Mã số thuế'),
    bankAccount: s('Tài khoản ngân hàng'),
    representative: s('Người đại diện'),
    representativeTitle: s('Chức vụ người đại diện'),
    logo: img('Logo doanh nghiệp'),
  }),
  quote: obj('Thông tin báo giá', {
    code: s('Mã báo giá', 'BG-2610-0001'),
    versionNo: n('Số phiên bản báo giá', '1'),
    date: d('Ngày báo giá'),
    validUntil: d('Ngày hết hạn hiệu lực'),
    issuedAt: d('Ngày phát hành ("" khi là bản nháp/xem trước)'),
    isDraft: b('true khi đang xem trước bản nháp (chưa phát hành)'),
    notes: s('Ghi chú hiển thị cho khách hàng'),
    pricesNote: s('Ghi chú về VAT/giá (từ cài đặt)'),
  }),
  salesperson: obj('Người báo giá', {
    name: s('Họ tên'),
    title: s('Chức danh'),
    phone: s('Điện thoại'),
    email: s('Email'),
  }),
  customer: obj('Khách hàng', {
    name: s('Tên khách hàng'),
    phone: s('Số điện thoại'),
    province: s('Tỉnh/thành phố'),
    address: s('Địa chỉ chi tiết'),
    typeLabel: s('Kiểu khách hàng'),
    note: s('Ghi chú khách hàng'),
  }),
  package: obj('Gói sản phẩm (phiên bản tại thời điểm báo giá)', {
    code: s('Mã gói'),
    name: s('Tên gói'),
    versionNo: n('Phiên bản gói', '3'),
    systemType: s('Loại hệ thống (nhãn)', 'Hybrid có lưu trữ'),
    systemTypeCode: s('Mã loại hệ thống: ONGRID | HYBRID | OFFGRID | OTHER'),
    capacityKwp: n('Công suất thực tế theo số tấm pin đã chọn (kWp)', '5,5'),
    nominalCapacityKwp: n('Công suất danh định tham khảo của gói (kWp)', '5,5'),
    description: s('Mô tả gói'),
    pricingMethod: s('Phương pháp định giá (nhãn)'),
    pricingMethodCode: s('FIXED | ITEMIZED | PER_KWP'),
    panelQuantity: n('Số tấm pin trong báo giá', '10'),
    panelDefaultQuantity: n('Số tấm pin chuẩn của gói', '10'),
    image: img('Ảnh đại diện gói'),
  }),
  items: arr(
    'Danh sách vật tư (theo thứ tự hiển thị). Dùng {{#each items}} ... {{/each}}',
    obj('Một dòng vật tư', {
      index: n('Số thứ tự (1, 2, 3...)', '1'),
      code: s('Mã vật tư'),
      name: s('Tên sản phẩm'),
      category: s('Danh mục'),
      technicalSpecs: s('Thông số kỹ thuật'),
      manufacturer: s('Hãng sản xuất'),
      model: s('Model'),
      quantity: n('Số lượng', '10'),
      unit: s('Đơn vị tính'),
      unitPrice: m('Đơn giá áp dụng'),
      amount: m('Thành tiền'),
      note: s('Ghi chú'),
      image: img('Ảnh sản phẩm (đã thu nhỏ)'),
      isBatteryOption: b('true nếu là pin lưu trữ mua thêm'),
    }),
  ),
  pricing: obj('Tổng hợp giá', {
    showItemPrices: b('true nếu gói tính theo danh mục vật tư (nên hiển thị đơn giá từng dòng)'),
    materialsTotal: m('Tổng tiền vật tư (theo đơn giá áp dụng)'),
    markupAmount: m('Khoản lợi nhuận/cộng thêm (gói tính theo vật tư)'),
    packageExtraCostsTotal: m('Chi phí cố định của gói (lắp đặt, vận chuyển... đã cấu hình)'),
    panelAdjustment: m('Điều chỉnh giá do thay đổi số tấm pin (gói giá cố định)'),
    hasPanelAdjustment: b('Có điều chỉnh do thay đổi số tấm pin'),
    packagePrice: m('Giá gói'),
    hasBattery: b('Có mua thêm pin lưu trữ'),
    batteryAmount: m('Thành tiền pin lưu trữ mua thêm'),
    hasExtraCosts: b('Có chi phí bổ sung'),
    extraCosts: arr(
      'Chi phí bổ sung của báo giá',
      obj('Khoản chi phí', { label: s('Tên khoản'), amount: m('Số tiền') }),
    ),
    extraCostsTotal: m('Tổng chi phí bổ sung'),
    subtotal: m('Tạm tính (trước chiết khấu)'),
    hasDiscount: b('Có chiết khấu'),
    discountPercent: p('Tỷ lệ chiết khấu'),
    discountAmount: m('Số tiền chiết khấu'),
    afterDiscount: m('Giá sau chiết khấu'),
    hasVat: b('Có áp dụng VAT (theo cài đặt)'),
    vatPercent: p('Thuế suất VAT', '8%'),
    vatAmount: m('Tiền thuế VAT'),
    total: m('Tổng thanh toán'),
    totalInWords: s('Tổng thanh toán bằng chữ', 'Một trăm hai mươi lăm triệu đồng'),
  }),
  financing: obj('Khoản vay (enabled = false nếu khách không vay)', {
    enabled: b('Có sử dụng khoản vay'),
    planName: s('Tên chương trình vay'),
    lenderName: s('Ngân hàng / đơn vị tài chính'),
    annualRate: p('Lãi suất năm', '9,5%'),
    monthlyRate: p('Lãi suất tháng quy đổi', '0,7917%'),
    method: s('Phương thức trả nợ (nhãn)'),
    methodCode: s('DECLINING_BALANCE | ANNUITY'),
    isAnnuity: b('true nếu trả đều hàng tháng (niên kim)'),
    rateConversion: s('Cách quy đổi lãi suất (nhãn)'),
    termMonths: n('Thời hạn vay (tháng)', '36'),
    termYears: n('Thời hạn vay (năm, có thể lẻ)', '3'),
    systemValue: m('Giá trị gói/hệ thống được tài trợ'),
    downPayment: m('Số tiền trả trước'),
    downPaymentPercent: p('Tỷ lệ trả trước'),
    loanAmount: m('Số tiền vay'),
    financingPercent: p('Tỷ lệ tài trợ'),
    monthlyPayment: m('Khoản trả hàng tháng (niên kim) / kỳ đầu (dư nợ giảm dần)'),
    firstPayment: m('Khoản trả kỳ đầu'),
    lastPayment: m('Khoản trả kỳ cuối'),
    totalPrincipal: m('Tổng tiền gốc'),
    totalInterest: m('Tổng tiền lãi'),
    processingFee: m('Phí xử lý hồ sơ'),
    totalPayment: m('Tổng phải trả ngân hàng (gốc + lãi + phí)'),
    totalCostToCustomer: m('Tổng chi phí khách hàng (trả trước + tổng phải trả)'),
    prepaymentPenaltyNote: s('Ghi chú phí trả trước hạn'),
    otherFeesNote: s('Ghi chú các khoản phí khác'),
    hasDueDates: b('Lịch trả nợ có ngày đến hạn (khi đã nhập ngày giải ngân)'),
    schedule: arr(
      'Lịch trả nợ theo kỳ',
      obj('Một kỳ', {
        period: n('Kỳ', '1'),
        dueDate: d('Ngày đến hạn ("" nếu không xác định)'),
        openingBalance: m('Dư nợ đầu kỳ'),
        principal: m('Gốc'),
        interest: m('Lãi'),
        payment: m('Tổng trả'),
        closingBalance: m('Dư nợ cuối kỳ'),
      }),
    ),
  }),
  payback: obj('Ước tính hoàn vốn đầu tư (khác thời hạn vay)', {
    enabled: b('Nhân viên đã bật phần hoàn vốn'),
    available: b('Đủ dữ liệu và có kết quả hoàn vốn'),
    statusMessage: s('Thông điệp kết quả / cảnh báo thiếu dữ liệu'),
    years: n('Số năm hoàn vốn', '6,3'),
    firstYearProductionKwh: n('Sản lượng năm đầu (kWh)', '7.300'),
    firstYearSavings: m('Tiết kiệm năm đầu'),
    monthlySavings: m('Tiết kiệm bình quân/tháng năm đầu'),
    assumptions: arr(
      'Giả định đã sử dụng',
      obj('Giả định', { label: s('Tên giả định'), value: s('Giá trị') }),
    ),
    missing: arr('Dữ liệu còn thiếu', s('Tên dữ liệu thiếu')),
  }),
  layout: obj('Bố cục do người báo giá chỉnh trước khi phát hành', {
    items: obj('Bảng danh mục vật tư', {
      showNote: b('Hiển thị cột Ghi chú (false khi người báo giá ẩn cột để ảnh vật tư to hơn)'),
      rowHeight: s('Chiều cao tối thiểu mỗi dòng ("" = tự động theo nội dung)', '18mm'),
      imageHeight: s('Chiều cao tối đa ảnh vật tư theo chiều cao dòng ("" = mặc định của mẫu)', '15mm'),
      widths: obj('Độ rộng cột (%, tổng các cột đang hiển thị = 100%; "" khi cột không hiển thị)', {
        index: s('Cột STT', '4%'),
        name: s('Cột Tên sản phẩm', '17%'),
        specs: s('Cột Thông số kỹ thuật', '18%'),
        manufacturer: s('Cột Hãng sản xuất', '9%'),
        quantity: s('Cột Số lượng', '5%'),
        unit: s('Cột Đơn vị tính', '5%'),
        image: s('Cột Hình ảnh', '9%'),
        unitPrice: s('Cột Đơn giá (chỉ khi pricing.showItemPrices)', '12%'),
        amount: s('Cột Thành tiền (chỉ khi pricing.showItemPrices)', '13%'),
        note: s('Cột Ghi chú', '8%'),
      }),
    }),
  }),
  meta: obj('Thông tin kỹ thuật', {
    generatedAt: s('Thời điểm tạo (dd/mm/yyyy HH:mm)'),
    contractVersion: s('Phiên bản hợp đồng dữ liệu', TEMPLATE_CONTRACT_VERSION),
    isPreview: b('true khi render ở chế độ xem trước'),
  }),
};

/** Các biến bắt buộc phải xuất hiện trong từng loại mẫu. */
export const REQUIRED_VARS: Record<TemplateKind, string[]> = {
  COVER_LETTER: ['company.name', 'customer.name'],
  PROPOSAL: ['quote.code', 'customer.name', 'pricing.total'],
  ITEMS: ['items', 'items[].name', 'items[].quantity'],
  FINANCING: ['financing.enabled', 'financing.loanAmount'],
  COMBINED: ['quote.code', 'customer.name', 'pricing.total', 'items', 'items[].name', 'financing.enabled'],
};

/** Helper Handlebars được phép trong template (ngoài các block mặc định if/unless/each/with). */
export const ALLOWED_HELPERS = ['if', 'unless', 'each', 'with', 'eq', 'ne', 'not', 'and', 'or'] as const;

// ---------- Zod schema sinh từ cây hợp đồng ----------
function nodeToZod(node: VarNode): z.ZodType {
  switch (node.type) {
    case 'boolean':
      return z.boolean();
    case 'object':
      return z.strictObject(
        Object.fromEntries(Object.entries(node.fields).map(([k, v]) => [k, nodeToZod(v)])),
      );
    case 'array':
      return z.array(nodeToZod(node.item));
    case 'image':
      return z
        .string()
        .refine(
          (v) => v === '' || /^data:image\/(png|jpeg|webp|gif|svg\+xml);base64,/.test(v),
          'Ảnh phải là data URI',
        );
    default:
      return z.string();
  }
}

export const quoteTemplateContextSchema = nodeToZod({
  type: 'object',
  desc: 'root',
  fields: TEMPLATE_CONTRACT,
});

/** Danh sách đường dẫn biến dạng phẳng, dùng cho tài liệu & gợi ý. "items[].name" = trường trong vòng lặp. */
export function flattenContract(
  nodes = TEMPLATE_CONTRACT,
  prefix = '',
): { path: string; type: string; desc: string; example?: string }[] {
  const out: { path: string; type: string; desc: string; example?: string }[] = [];
  for (const [k, v] of Object.entries(nodes)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v.type === 'object') {
      out.push({ path, type: 'object', desc: v.desc });
      out.push(...flattenContract(v.fields, path));
    } else if (v.type === 'array') {
      out.push({ path, type: 'array', desc: v.desc });
      if (v.item.type === 'object') out.push(...flattenContract(v.item.fields, `${path}[]`));
      else out.push({ path: `${path}[]`, type: v.item.type, desc: v.item.desc });
    } else out.push({ path, type: v.type, desc: v.desc, example: v.example });
  }
  return out;
}
